import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import { printJobsApi } from '../lib/api';
import { useLabelPrinters } from '../hooks/useLabelPrinters';
import { formatDate, formatApiErrorDetail } from '../lib/utils';
import PageHeader from '../components/layout/PageHeader';
import PrintPreviewDialog from '../components/PrintPreviewDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { CircleNotch, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  pending_assign: 'bg-amber-500/15 text-amber-700',
  pending_local_print: 'bg-amber-500/15 text-amber-700',
  assigned: 'bg-brand-500/15 text-brand-600',
  printed_locally: 'bg-green-500/15 text-green-600',
  completed: 'bg-green-500/15 text-green-600',
  cancelled: 'bg-zinc-500/15 text-zinc-500',
  submitted: 'bg-brand-500/15 text-brand-600',
  ready: 'bg-amber-500/15 text-amber-700',
};

function statusLabelKey(displayStatus) {
  return `print.status_${displayStatus}`;
}

export default function PrintJobsPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionJobId, setActionJobId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLabels, setPreviewLabels] = useState([]);
  const [previewJob, setPreviewJob] = useState(null);
  const { selectedPrinter } = useLabelPrinters();

  const loadJobs = useCallback(async () => {
    try {
      const res = await printJobsApi.list();
      setJobs(res.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load print jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleLocalPrint = async (job) => {
    setActionJobId(job.job_id);
    try {
      const detail = await printJobsApi.get(job.job_id);
      const qrs = detail.data?.qr_codes || [];
      if (!qrs.length) {
        toast.error('No labels in this print job');
        return;
      }
      const labels = qrs.map((q) => ({
        qr_id: q.qr_id,
        qr_image: q.qr_image,
        unit_label: q.unit_label,
        unit_code: q.unit_code,
        unit_display: q.unit_display,
        public_url: q.public_url,
      }));
      if (!labels.some((l) => l.qr_image)) {
        toast.error('QR images are missing for this print job');
        return;
      }
      setPreviewLabels(labels);
      setPreviewJob(job);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load labels');
    } finally {
      setActionJobId(null);
    }
  };

  const handlePrinted = async () => {
    if (!previewJob?.job_id) return;
    try {
      const res = await printJobsApi.localPrint(previewJob.job_id);
      const n = res.data?.printed_count ?? 0;
      toast.success(t('print.localPrintRecorded', { count: n }));
      setPreviewOpen(false);
      loadJobs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to record print');
    }
  };

  const canAct = (job) =>
    job.display_status === 'pending_assign' || job.display_status === 'pending_local_print';

  const labelForStatus = (job) => {
    const ds = job.display_status || job.status;
    return t(statusLabelKey(ds), { defaultValue: ds });
  };

  const printerProfile = selectedPrinter && previewJob
    ? {
        ...selectedPrinter,
        width_mm: previewJob.print_unit === 'cm' ? previewJob.print_width * 10 : previewJob.print_width,
        height_mm: previewJob.print_unit === 'cm' ? previewJob.print_height * 10 : previewJob.print_height,
      }
    : selectedPrinter;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="print-jobs-page">
        <PageHeader
          title={t('print.jobsTitle')}
          description="Review pending jobs, preview labels, then print or download."
        />

        <Card>
          <CardHeader>
            <CardTitle>{t('print.allJobs')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <CircleNotch className="h-8 w-8 animate-spin text-brand-500" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>{t('print.batch')}</TableHead>
                    <TableHead>Labels</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                        {t('print.noJobs')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job) => (
                      <TableRow key={job.job_id}>
                        <TableCell className="font-mono text-sm">{job.job_id}</TableCell>
                        <TableCell className="max-w-[8rem] truncate font-mono text-xs" title={job.master_qr_id}>
                          {job.master_qr_id?.slice(0, 8)}…
                        </TableCell>
                        <TableCell>{job.qr_ids?.length || 0}</TableCell>
                        <TableCell>
                          {job.print_width}×{job.print_height} {job.print_unit}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE[job.display_status] || STATUS_BADGE.ready}>
                            {labelForStatus(job)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(job.created_at)}</TableCell>
                        <TableCell className="text-right">
                          {canAct(job) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actionJobId === job.job_id}
                              onClick={() => handleLocalPrint(job)}
                            >
                              {actionJobId === job.job_id ? (
                                <CircleNotch className="h-4 w-4 animate-spin" />
                              ) : (
                                <Printer className="mr-1 h-4 w-4" />
                              )}
                              {t('print.printLocally')}
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <PrintPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        labels={previewLabels}
        title={previewJob ? `Job ${previewJob.job_id}` : 'Print job'}
        sheetWidth={previewJob?.print_width}
        sheetHeight={previewJob?.print_height}
        sheetUnit={previewJob?.print_unit || 'mm'}
        printerProfile={printerProfile}
        onPrinted={handlePrinted}
      />
    </DashboardLayout>
  );
}
